import { Loader, Modal, Table, TableSearch } from '@adtech/ui';
import ReportMessage from '@components/ReportMessage';
import snackbarsConstants from '@constants/snackbars';
import useProjects from '@hooks/useProjects';
import useSnackbar from '@hooks/useSnackbar';
import { useAppSelector } from '@hooks/useStore';
import IconTrash from '@images/svg/icons/icon-trash.svg';
import { IUsersValue } from '@typings/counter';
import React, { FC, useEffect, useRef, useState } from 'react';
import s from './AccessUsers.pcss';
import PermissionSelect, { EAccessValues } from './Fields/PermissionSelect';

interface IProps {
    usersListProp: IUsersValue[];
}

const UsersTable: FC<IProps> = ({ usersListProp }) => {
    const currentEmail = useAppSelector((state) => state.userSlice.email);
    const currentProject = useAppSelector((state) => state.projectsSlice.current);
    const saveCounterRequest = useAppSelector((state) => state.projectsSlice.saveCounterRequest);
    const [usersList, setUsersList] = useState<IUsersValue[]>(usersListProp);
    const [isDeleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
    const userDeletedEmail = useRef<string>(null);
    const searchValue = useRef<string>(null);

    const { setSnackbar } = useSnackbar();
    const {
        api: { editProjectUsers },
    } = useProjects();

    const searchHandler = (value: string) => {
        searchValue.current = value;
        const newList = value ? usersListProp.filter((user) => user.email.includes(value)) : [...usersListProp];
        setUsersList(newList);
    };

    useEffect(() => {
        setUsersList([...usersListProp]);
        searchHandler(searchValue.current);
    }, [JSON.stringify(usersListProp)]);

    const editAccessSettings = (usersListParam: IUsersValue[]) => {
        const successCallback = () => {
            setSnackbar(snackbarsConstants.ACCESS_CHANGED);
        };
        editProjectUsers(currentProject.id, usersListParam, successCallback);
    };

    const deleteIconClickHandler = (email: string) => {
        userDeletedEmail.current = email;
        setDeleteUserModalOpen(true);
    };

    const deleteUser = () => {
        setDeleteUserModalOpen(false);

        const newUsers = usersListProp.filter((user) => user.email !== userDeletedEmail.current);

        editAccessSettings(newUsers);
    };

    const changeUserAccessLevel = (email: string, accessLevel: EAccessValues) => {
        const newUsers = usersListProp.map((user) => {
            if (user.email === email) return { ...user, accessLevel };

            return user;
        });

        editAccessSettings(newUsers);
    };

    const renderAccess = (user: IUsersValue) => {
        const isCurrentUser = user.email === currentEmail;

        return (
            <PermissionSelect
                className={s.usersTableSelect}
                disabled={isCurrentUser}
                size="small"
                value={user.accessLevel}
                setValue={(value) => changeUserAccessLevel(user.email, value)}
            />
        );
    };

    const tableColumns = [
        {
            title: 'Адрес электронной почты',
            dataIndex: 'email',
            render: (email: string) => {
                const isCurrentUser = email === currentEmail;
                const emailClass = isCurrentUser ? s.usersTableEmailCurrent : '';
                return (
                    <div className={emailClass}>
                        {email} {isCurrentUser && '(это вы)'}
                    </div>
                );
            },
        },
        {
            title: 'Тип доступа',
            dataIndex: 'accessLevel',
            render: renderAccess,
        },
        {
            title: '',
            dataIndex: 'icon',
            render: (user: IUsersValue) => {
                const isCurrentUser = user.email === currentEmail;
                return (
                    !isCurrentUser && (
                        <div className={s.usersTableIconWrap}>
                            <IconTrash
                                className={s.usersTableRemoveIcon}
                                onClick={() => deleteIconClickHandler(user.email)}
                            />
                        </div>
                    )
                );
            },
        },
    ];

    const prepareDataForRender = (user: IUsersValue) => {
        const { email } = user;

        return {
            key: email,
            email,
            accessLevel: user,
            icon: user,
        };
    };

    const renderEmptyText = <ReportMessage className={s.usersTableNoResults} message="Нет данных для отображения" />;

    return (
        <Loader loading={saveCounterRequest}>
            <div className={s.usersTableSearch}>
                <TableSearch onSearch={searchHandler} searchPlaceholder="Введите email" />
            </div>
            <Table
                className={s.usersTable}
                columns={tableColumns}
                dataSource={usersList?.map((user: IUsersValue) => prepareDataForRender(user))}
                pagination={false}
                locale={{ emptyText: renderEmptyText }}
            />
            <Modal
                isOpened={isDeleteUserModalOpen}
                title="Удаление пользователя"
                okText="Подтвердить"
                cancelText="Отменить"
                onOk={deleteUser}
                onClose={() => setDeleteUserModalOpen(false)}
                closeOnClickOutside={false}
                centered
                size="sm"
            >
                <p className={s.usersTableModalText}>
                    Вы&nbsp;собираетесь удалить выбранного пользователя из&nbsp;списка пользователей. Вернуть его можно
                    будет, повторно предоставив доступ.
                </p>
            </Modal>
        </Loader>
    );
};

export default UsersTable;
